import React, { useState } from "react";
import { FaPhone, FaEnvelope, FaCheckCircle, FaExclamationCircle } from "react-icons/fa";
import contentConfig from "../content.config.json";
import "../Styles/Contact.css";

const iconMap = {
  phone: FaPhone,
  envelope: FaEnvelope,
};

const validateEmail = (email) =>
  /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email);

const Contact = () => {
  const {
    title,
    subtitle,
    description,
    info,
    form,
    formTitle,
    successMessage,
  } = contentConfig.contact;

  const initialState = form.fields.reduce((acc, field) => {
    acc[field.name] = "";
    return acc;
  }, {});

  const [formData, setFormData] = useState(initialState);
  const [status, setStatus] = useState({ state: "idle", message: "" });
  const [fieldErrors, setFieldErrors] = useState({});

  const APPS_SCRIPT_URL =
    process.env.REACT_APP_CONTACT_ENDPOINT ||
    "https://script.google.com/macros/s/REPLACE_WITH_DEPLOYED_SCRIPT_ID/exec";

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (fieldErrors[name]) {
      setFieldErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const validate = () => {
    const errors = {};
    form.fields.forEach((field) => {
      const value = (formData[field.name] || "").trim();
      if (field.required && !value) {
        errors[field.name] = `${field.placeholder} is required`;
      }
      if (field.type === "email" && value && !validateEmail(value)) {
        errors[field.name] = "Please enter a valid email address";
      }
    });
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validate();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setStatus({
        state: "error",
        message: "Please correct the highlighted fields and try again.",
      });
      return;
    }

    setStatus({ state: "loading", message: "" });

    try {
      // Use text/plain to avoid CORS preflight against Google Apps Script.
      const response = await fetch(APPS_SCRIPT_URL, {
        method: "POST",
        mode: "cors",
        headers: { "Content-Type": "text/plain;charset=utf-8" },
        body: JSON.stringify({ ...formData, source: "pipbros-website" }),
      });

      const result = await response.json().catch(() => ({}));

      if (!response.ok || result.status === "error") {
        throw new Error(result.message || "Submission failed");
      }

      setStatus({ state: "success", message: successMessage.message });
      setFormData(initialState);
    } catch (err) {
      setStatus({
        state: "error",
        message:
          "We couldn't send your message. Please email admin@pipbros.com directly and we'll respond promptly.",
      });
    }
  };

  const isLoading = status.state === "loading";
  const showSuccess = status.state === "success";

  return (
    <div className="contact-page page-shell">
      <section className="page-hero">
        <div className="page-hero-inner">
          <span className="section-badge">{subtitle}</span>
          <h1 className="page-hero-title">{title}</h1>
          <p className="page-hero-subtitle">{description}</p>
        </div>
      </section>

      <div className="contact-container">
        <div className="contact-grid">
          <aside className="contact-aside">
            <h2 className="contact-aside-title">Direct lines</h2>
            <p className="contact-aside-text">
              Prefer to reach out directly? Either of us is happy to talk
              through a project, an idea, or a problem.
            </p>

            <ul className="contact-info-list">
              {info.map((item, index) => {
                const Icon = iconMap[item.icon];
                return (
                  <li key={index} className="contact-info-item">
                    <span className="contact-info-icon">
                      {Icon && <Icon />}
                    </span>
                    <div className="contact-info-content">
                      <span className="contact-info-label">{item.label}</span>
                      {item.link ? (
                        <a href={item.link} className="contact-info-value">
                          {item.value}
                        </a>
                      ) : (
                        <span className="contact-info-value">{item.value}</span>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </aside>

          <section className="contact-form-section" aria-labelledby="contact-form-title">
            <h2 id="contact-form-title" className="contact-form-title">
              {formTitle}
            </h2>

            {showSuccess ? (
              <div className="contact-success" role="status">
                <div className="contact-success-icon">
                  <FaCheckCircle />
                </div>
                <h3>{successMessage.title}</h3>
                <p>{status.message}</p>
                <button
                  type="button"
                  className="contact-success-action"
                  onClick={() => setStatus({ state: "idle", message: "" })}
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="contact-form" noValidate>
                <div className="form-row">
                  {form.fields.slice(0, 2).map((field) => (
                    <div key={field.name} className="form-field">
                      <label htmlFor={field.name}>{field.placeholder}</label>
                      <input
                        id={field.name}
                        type={field.type}
                        name={field.name}
                        placeholder={field.placeholder}
                        value={formData[field.name]}
                        onChange={handleChange}
                        required={field.required}
                        aria-invalid={!!fieldErrors[field.name]}
                      />
                      {fieldErrors[field.name] && (
                        <span className="form-field-error">
                          {fieldErrors[field.name]}
                        </span>
                      )}
                    </div>
                  ))}
                </div>

                {form.fields.slice(2).map((field) => (
                  <div key={field.name} className="form-field">
                    <label htmlFor={field.name}>{field.placeholder}</label>
                    {field.type === "textarea" ? (
                      <textarea
                        id={field.name}
                        name={field.name}
                        placeholder={field.placeholder}
                        value={formData[field.name]}
                        onChange={handleChange}
                        required={field.required}
                        rows={6}
                        aria-invalid={!!fieldErrors[field.name]}
                      />
                    ) : (
                      <input
                        id={field.name}
                        type={field.type}
                        name={field.name}
                        placeholder={field.placeholder}
                        value={formData[field.name]}
                        onChange={handleChange}
                        required={field.required}
                        aria-invalid={!!fieldErrors[field.name]}
                      />
                    )}
                    {fieldErrors[field.name] && (
                      <span className="form-field-error">
                        {fieldErrors[field.name]}
                      </span>
                    )}
                  </div>
                ))}

                {status.state === "error" && (
                  <div className="form-banner form-banner--error" role="alert">
                    <FaExclamationCircle />
                    <span>{status.message}</span>
                  </div>
                )}

                <button
                  type="submit"
                  className="submit-button"
                  disabled={isLoading}
                >
                  {isLoading ? "Sending..." : form.submitText}
                </button>
              </form>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default Contact;
